import { useEffect, useState } from 'react'
import clsx from 'clsx'

/**
 * Compact floating promo banner designed to sit in the left sidebar
 * (dark zinc-800 background, below the nav). Matches the user's reference:
 * icon on the left, title + description middle, CTA button right, × in corner.
 *
 * Two variants:
 *   welcome — new user, 10% off first purchase (no urgency countdown)
 *   comeback — returning user, 20% off with 48h urgency countdown
 */
const PromoBanner = ({ banner, onCta, onDismiss }) => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!banner?.expiresAt) {
      setTimeLeft('')
      return
    }
    const tick = () => {
      const msLeft = new Date(banner.expiresAt).getTime() - Date.now()
      if (msLeft <= 0) {
        setTimeLeft('')
        return
      }
      const h = Math.floor(msLeft / 3600000)
      const m = Math.floor((msLeft % 3600000) / 60000)
      setTimeLeft(`${h}ч ${m}м`)
    }
    tick()
    const interval = setInterval(tick, 60000) // update every minute
    return () => clearInterval(interval)
  }, [banner?.expiresAt])

  if (!banner) return null

  const isWelcome = banner.type === 'welcome'

  return (
    <div className="relative mx-3 mb-3">
      {/* Dismiss × — absolute top-right corner */}
      <button
        onClick={onDismiss}
        className="absolute top-1.5 right-1.5 w-5 h-5 rounded flex items-center justify-center text-slate-500 hover:text-slate-200 hover:bg-white/10 transition-colors z-10"
        title="Скрыть"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="rounded-lg border border-white/10 bg-zinc-900/60 backdrop-blur-sm p-3 pr-6">
        {/* Icon + title row */}
        <div className="flex items-center gap-2 mb-1.5">
          <div
            className={clsx(
              'flex-shrink-0 w-6 h-6 rounded flex items-center justify-center',
              isWelcome ? 'bg-brand-lime/20 text-brand-lime' : 'bg-rose-500/20 text-rose-300'
            )}
          >
            {isWelcome ? (
              /* Rocket icon — welcome */
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
              </svg>
            ) : (
              /* Heart icon — comeback */
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold text-white leading-tight truncate">{banner.title}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-[11px] text-slate-400 leading-snug mb-2.5">
          {banner.description}
          {timeLeft && (
            <span className="block mt-0.5 text-rose-300 font-medium">
              Осталось {timeLeft}
            </span>
          )}
        </p>

        {/* CTA button — full width, compact */}
        <button
          onClick={onCta}
          className={clsx(
            'w-full inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors',
            isWelcome
              ? 'bg-brand-lime text-zinc-900 hover:bg-brand-lime/90'
              : 'bg-rose-500 text-white hover:bg-rose-600'
          )}
        >
          {isWelcome ? `Скидка ${banner.discount}%` : `Вернуться со скидкой ${banner.discount}%`}
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default PromoBanner
